<template>
    <transition
        enter-active-class="duration-200 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="duration-100 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
    >
        <PopoverPanel
            focus
            class="absolute z-30 top-0 inset-x-0 p-2 transition transform origin-top-left md:hidden"
        >
            <div
                class="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y-2 divide-gray-50"
            >
                <div class="pt-5 pb-6 px-5 sm:pb-8">
                    <div class="flex items-center justify-between">
                        <div class="-ml-2">
                            <PopoverButton
                                class="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
                            >
                                <span class="sr-only">Close menu</span>
                                <XIcon class="h-6 w-6" aria-hidden="true" />
                            </PopoverButton>
                        </div>

                        <a
                            :href="phoneLink"
                            class="rounded-md p-2 inline-flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 focus:outline-none"
                        >
                            <span>Need Help?</span>
                            <PhoneIcon
                                class="ml-2 h-6 w-6"
                                aria-hidden="true"
                            />
                        </a>
                    </div>

                    <div v-if="product">
                        <div class="mt-6 sm:mt-8">
                            <nav>
                                <div
                                    @click="toggleProductMenu"
                                    :class="[
                                        openProductMenu
                                            ? `mb-3 -mx-5 ${bgProductColor}`
                                            : '-mb-3 -mx-3 hover:bg-gray-50',
                                        '-mt-4 sm:-mt-6 mb-3 p-3 flex items-center cursor-pointer',
                                    ]"
                                >
                                    <span class="flex items-center">
                                        <div
                                            :class="[
                                                bgProductColor,
                                                'shrink-0 flex items-center justify-center h-10 w-10 sm:h-16 sm:w-16 rounded-md text-white',
                                            ]"
                                        >
                                            <component
                                                :is="
                                                    openProductMenu
                                                        ? ChevronUpIcon
                                                        : ChevronDownIcon
                                                "
                                                class="h-7 w-7 sm:h-12 sm:w-12"
                                                aria-hidden="true"
                                            />
                                        </div>

                                        <div
                                            :class="[
                                                openProductMenu
                                                    ? 'text-white'
                                                    : 'text-gray-900',
                                                'ml-4 text-base font-medium',
                                            ]"
                                        >
                                            {{ product.name }}
                                        </div>
                                    </span>
                                </div>

                                <transition
                                    enter-active-class="transition transform origin-top duration-200 ease-out"
                                    enter-from-class="transition transform origin-top opacity-0 -translate-y-2"
                                    enter-to-class="transition transform origin-top opacity-100"
                                >
                                    <div v-if="openProductMenu">
                                        <div
                                            class="grid gap-7 sm:grid-cols-2 sm:gap-y-8 sm:gap-x-4"
                                        >
                                            <a
                                                v-for="page in productPages"
                                                :key="page.id"
                                                :href="page.href"
                                                class="-m-3 flex items-center p-3 rounded-lg hover:bg-gray-50"
                                            >
                                                <div
                                                    :class="[
                                                        bgProductColor,
                                                        'shrink-0 flex items-center justify-center h-10 w-10 sm:h-16 sm:w-16 rounded-md text-white',
                                                    ]"
                                                >
                                                    <svg-vue
                                                        class="h-7 w-7 sm:h-12 sm:w-12"
                                                        :icon="page.svg"
                                                        :key="page.id"
                                                        aria-hidden="true"
                                                    />
                                                </div>

                                                <div
                                                    class="ml-4 text-base font-medium text-gray-900"
                                                >
                                                    {{ page.name }}
                                                </div>
                                            </a>
                                        </div>

                                        <div
                                            class="-mx-5 mt-3 -mb-3 border-b-2 border-gray-50"
                                        ></div>
                                    </div>
                                </transition>
                            </nav>
                        </div>
                    </div>

                    <div class="mt-6 sm:mt-8">
                        <nav>
                            <div
                                v-if="product"
                                @click="toggleProductCategoriesMenu"
                                :class="[
                                    openProductCategoriesMenu
                                        ? 'mb-3 -mx-5 bg-nutreance-green text-white'
                                        : '-mb-3 -mx-3 hover:bg-gray-50',
                                    '-mt-4 sm:-mt-6 p-3 flex items-center cursor-pointer',
                                ]"
                            >
                                <span class="flex items-center">
                                    <div
                                        class="shrink-0 flex items-center justify-center h-10 w-10 sm:h-16 sm:w-16 rounded-md bg-nutreance-green text-white"
                                    >
                                        <component
                                            :is="
                                                openProductCategoriesMenu
                                                    ? ChevronUpIcon
                                                    : ChevronDownIcon
                                            "
                                            class="h-7 w-7 sm:h-12 sm:w-12"
                                            aria-hidden="true"
                                        />
                                    </div>

                                    <div
                                        :class="[
                                            openProductCategoriesMenu
                                                ? 'text-white'
                                                : 'text-gray-900',
                                            'ml-4 text-base font-medium',
                                        ]"
                                    >
                                        Products
                                    </div>
                                </span>
                            </div>

                            <transition
                                enter-active-class="transition transform origin-top duration-200 ease-out"
                                enter-from-class="transition transform origin-top opacity-0 -translate-y-2"
                                enter-to-class="transition transform origin-top opacity-100"
                            >
                                <div v-if="openProductCategoriesMenu">
                                    <div
                                        v-if="loading"
                                        class="py-12 text-center"
                                    >
                                        <RefreshIcon
                                            class="mx-auto h-24 w-24 animate-spin text-gray-400"
                                        />
                                    </div>

                                    <div
                                        v-else-if="!productCategory"
                                        class="grid gap-7 sm:grid-cols-2 sm:gap-y-8 sm:gap-x-4"
                                    >
                                        <button
                                            role="button"
                                            v-for="category in productCategories"
                                            :key="category.id"
                                            @click="productCategory = category"
                                            class="-m-3 flex items-center p-3 rounded-lg hover:bg-gray-50 focus:outline-none"
                                        >
                                            <span
                                                class="shrink-0 flex items-center justify-center h-10 w-10 sm:h-16 sm:w-16 rounded-md bg-nutreance-green text-nutreance-green"
                                            >
                                                <svg-vue
                                                    class="h-7 w-7 sm:h-12 sm:w-12"
                                                    :icon="category.svg"
                                                    :key="category.svg"
                                                    aria-hidden="true"
                                                />
                                            </span>

                                            <span
                                                class="ml-4 text-base font-medium text-gray-900"
                                            >
                                                {{ category.name }}
                                            </span>
                                        </button>
                                    </div>

                                    <div
                                        v-else
                                        class="grid gap-7 sm:grid-cols-2 sm:gap-y-8 sm:gap-x-4"
                                    >
                                        <button
                                            role="button"
                                            @click="productCategory = null"
                                            class="-m-3 flex items-center p-3 rounded-lg hover:bg-gray-50 focus:outline-none"
                                        >
                                            <span
                                                class="shrink-0 flex items-center justify-center h-10 w-10 sm:h-16 sm:w-16 rounded-md bg-nutreance-green text-white"
                                            >
                                                <ReplyIcon
                                                    class="h-7 w-7 sm:h-12 sm:w-12"
                                                    aria-hidden="true"
                                                />
                                            </span>

                                            <span
                                                class="ml-4 text-base font-medium text-gray-900"
                                            >
                                                Back
                                            </span>
                                        </button>

                                        <a
                                            v-for="item in productCategory.products"
                                            :key="item.id"
                                            :href="`/products/${item.slug}`"
                                            class="-m-3 flex items-center p-3 rounded-lg hover:bg-gray-50"
                                        >
                                            <div
                                                class="shrink-0 flex items-center justify-center h-10 w-10 sm:h-16 sm:w-16"
                                            >
                                                <ProductIcon
                                                    :imgClass="'h-10 sm:h-16'"
                                                    :product="item"
                                                />
                                            </div>

                                            <div
                                                class="ml-4 w-full text-base font-medium text-gray-900"
                                            >
                                                <p
                                                    class="text-base font-semibold text-gray-900"
                                                >
                                                    {{ item.name }}
                                                </p>
                                                <p
                                                    class="text-sm font-medium text-gray-700"
                                                >
                                                    {{ item.description }}
                                                </p>
                                            </div>
                                        </a>
                                    </div>
                                </div>
                            </transition>
                        </nav>
                    </div>
                </div>

                <div class="py-6 px-5">
                    <div class="grid grid-cols-2 gap-4">
                        <a
                            v-for="link in links"
                            :key="link.name"
                            :href="link.href"
                            class="rounded-md text-base font-medium text-gray-900 hover:text-gray-700"
                        >
                            {{ link.name }}
                        </a>
                    </div>
                    <div v-if="isGuest" class="mt-6">
                        <a
                            href="/register"
                            class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker"
                        >
                            Create an Account
                        </a>

                        <p
                            class="mt-6 text-center text-base font-medium text-gray-500"
                        >
                            Have an account?

                            <a
                                href="/login"
                                class="text-nutreance-green hover:text-nutreance-green-lighter"
                            >
                                Sign In
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </PopoverPanel>
    </transition>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { PopoverButton, PopoverPanel } from "@headlessui/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/solid";
import {
    PhoneIcon,
    RefreshIcon,
    ReplyIcon,
    XIcon,
} from "@heroicons/vue/outline";
import ProductIcon from "../products/ProductIcon.vue";

const links = [
    {
        name: "Return Policy",
        href: "/return-policy",
    },
    {
        name: "Earn Coupons",
        href: "/coupons/send",
    },
    {
        name: "Contact Us",
        href: "/contact-us",
    },
    {
        name: "FAQ",
        href: "/questions",
    },
    {
        name: "About Us",
        href: "/about",
    },
    {
        name: "Quality Control",
        href: "/quality-control",
    },
];

const props = defineProps<{
    headquarters?: any;
    isGuest?: string;
    loading?: boolean;
    phone?: string;
    phoneLink?: string;
    product?: any;
    products?: any;
    productCategories?: any;
    productPages?: any;
}>();

const openProductMenu = ref(false);
const openProductCategoriesMenu = ref(!props.product);
const bgProductColor = computed(() => `bg-${props.product.slug}`);
const productCategory = ref<any>(null);

function toggleProductMenu(): void {
    openProductMenu.value = !openProductMenu.value;
}

function toggleProductCategoriesMenu(): void {
    openProductCategoriesMenu.value = !openProductCategoriesMenu.value;
}
</script>
